import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import DesktopModal from '@components/Modal/DesktopModal.tsx';
import MobileModal from '@components/Modal/MobileModal.tsx';
import { useIsMobile } from '@hooks/useIsMobile.tsx';

import { isTopOverlay, pushOverlay } from './overlayStack';

type ModalProps = {
  open?: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: ReactNode;
  closable?: boolean;
  haveActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  size?: 'default' | 'full';
  /** Desktop only: 960px instead of 700px, for a form with a preview column beside it. */
  wide?: boolean;
  /** Pinned under the content, which scrolls above it — e.g. a form's buttons. */
  footer?: ReactNode;
};

const Modal = ({
  open = false,
  onClose,
  children,
  title,
  closable = true,
  haveActions = false,
  onEdit,
  onDelete,
  size = 'default',
  wide = false,
  footer,
}: ModalProps) => {
  const isMobile = useIsMobile();
  const modalRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const containerEl = useMemo(() => {
    const el = document.createElement('div');
    el.setAttribute('data-modal-portal', '');
    return el;
  }, []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const removeOverlay = pushOverlay(containerEl);
    const isTopmost = () => isTopOverlay(containerEl);

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopmost()) return;

      if (e.key === 'Escape' && closable) {
        onCloseRef.current();
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusable = Array.from(
          modalRef.current.querySelectorAll<HTMLElement>(
            [
              'a[href]',
              'button:not([disabled])',
              'textarea:not([disabled])',
              'input:not([disabled])',
              'select:not([disabled])',
              '[tabindex]:not([tabindex="-1"])',
            ].join(','),
          ),
        ).filter((el) => el.offsetParent !== null);

        if (!focusable.length) {
          e.preventDefault();
          modalRef.current.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const isShift = e.shiftKey;

        if (!isShift && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }

        if (isShift && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    previouslyFocusedElement.current =
      (document.activeElement as HTMLElement | null) ?? previouslyFocusedElement.current;
    modalRef.current?.focus();

    const previousOverflow = document.body.style.overflow;

    document.addEventListener('keydown', onKeyDown);

    if (!isMobile) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      removeOverlay();
      document.removeEventListener('keydown', onKeyDown);
      if (!isMobile) {
        document.body.style.overflow = previousOverflow;
      }
      previouslyFocusedElement.current?.focus?.();
    };
  }, [closable, containerEl, isMobile, open]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.appendChild(containerEl);
    return () => {
      if (containerEl.parentNode) {
        containerEl.parentNode.removeChild(containerEl);
      }
    };
  }, [containerEl, open]);

  if (!open) {
    return null;
  }

  if (isMobile) {
    return createPortal(
      <MobileModal
        disablePortal
        maxHeight="90dvh"
        fullScreen={size === 'full'}
        title={title}
        open={open}
        onClose={onClose}
        containerRef={modalRef}
        closable={closable}
        haveActions={haveActions}
        onEdit={onEdit}
        onDelete={onDelete}
        footer={footer}
      >
        {children}
      </MobileModal>,
      containerEl,
    );
  }

  return createPortal(
    <DesktopModal
      title={title}
      onClose={onClose}
      containerRef={modalRef}
      closable={closable}
      haveActions={haveActions}
      onEdit={onEdit}
      onDelete={onDelete}
      wide={wide}
      footer={footer}
    >
      {children}
    </DesktopModal>,
    containerEl,
  );
};

export default Modal;
